<template>
  <div class="upload-images">
    <Sidebar />
    <div class="main-content">
      <!-- 首页轮播图编辑 -->
      <div class="section">
        <h3 class="section-title">编辑首页轮播大图</h3>
        <div class="carousel-upload">
          <div class="upload-area">
            <el-upload
              ref="carouselUploaderRef"
              :show-file-list="false"
              :before-upload="beforeImageUpload"
              :on-change="handleCarouselFileSelect"
              :auto-upload="false"
              accept="image/*"
              multiple
              :disabled="isUploading"
            >
              <template #trigger>
                <button class="upload-btn" :disabled="isUploading">
                  <i class="fas fa-upload"></i> {{ isUploading ? '上传中...' : '上传轮播图' }}
                </button>
              </template>
            </el-upload>
          </div>

          <!-- 轮播图列表 -->
          <div class="image-list">

            <!-- 可拖拽的用户上传图片列表 -->
            <draggable
              v-model="userCarouselImages"
              item-key="id"
              tag="div"
              class="draggable-area"
              :animation="200"
              @end="onCarouselDragEnd"
              :disabled="isUploading"
            >
              <template #item="{ element: image, index }">
                <div class="image-item">
                  <el-image
                    style="width: 200px; height: 200px"
                    :src="image.url"
                    :preview-src-list="allCarouselImageUrlsForPreview" 
                    :initial-index="index + 1" 
                    :preview-teleported="true"
                    fit="cover"
                  />
                  <!-- 删除按钮 -->
                  <button class="delete-btn" @click="deleteUserCarouselImage(index)" :disabled="isUploading">
                    <i class="fas fa-times"></i>
                  </button>
                  <!-- 上传状态 -->
                  <div v-if="image.status === 'uploading'" class="upload-status">上传中...</div>
                </div>
              </template>
            </draggable>
          </div>

          <!-- 保存按钮 -->
          <button class="save-btn" @click="saveCarouselImages" :disabled="isUploading || isSaving">
            {{ isSaving ? '保存中...' : '保存轮播图' }}
          </button>
        </div>
      </div>

      <!-- 首页产品大图编辑 -->
      <div class="section">
        <h3 class="section-title">编辑首页产品大图</h3>
        <div class="product-images">
          <div v-for="cat in categories" :key="cat.key" class="category-section">
            <h4 class="category-title">{{ cat.label }}</h4>
            <div class="upload-area">
              <el-upload
                :ref="el => productImageUploaderRefs[cat.key] = el"
                :show-file-list="false"
                :before-upload="beforeImageUpload"
                :on-change="file => handleProductFileSelect(cat.key, file)"
                :auto-upload="false"
                accept="image/*"
                :disabled="isUploading"
              >
                 <template #trigger>
                    <button class="upload-btn" :disabled="isUploading">
                      <i class="fas fa-upload"></i> {{ isUploading ? '上传中...' : '上传图片' }}
                    </button>
                 </template>
              </el-upload>
            </div>
            <!-- 产品图片预览 -->
            <div v-if="productImages[cat.key]" class="image-preview">
              <el-image
                style="width: 100%; height: 450px"
                :src="productImages[cat.key].displayUrl || productImages[cat.key].url"
                :preview-src-list="[productImages[cat.key].url]"
                :preview-teleported="true"
                fit="cover"
              />
              <!-- 删除按钮 -->
              <button class="delete-btn" @click="deleteProductImage(cat.key)" :disabled="isUploading">
                <i class="fas fa-times"></i>
              </button>
              <!-- 上传状态 -->
              <div v-if="productImages[cat.key].status === 'uploading'" class="upload-status">上传中...</div>
            </div>
            <!-- 无图片占位符 -->
            <div v-else class="no-image-placeholder">
              暂无图片
            </div>
          </div>
        </div>
         <!-- 保存按钮 -->
        <button class="save-btn" @click="saveProductImages" :disabled="isUploading || isSaving">
          {{ isSaving ? '保存中...' : '保存产品图片' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue';
import Sidebar from '../components/Sidebar.vue'; // 确认路径
import { ElMessage, ElUpload, ElImage } from 'element-plus';
import draggable from 'vuedraggable';
// --- 导入新的 API 和 Service ---
import { getQiniuToken } from '@/api/qiniu'; // 从优化后的 qiniu.js 导入
import { uploadToQiniu } from '@/services/uploader'; // 从优化后的 uploader.js 导入
import { getNavPicName, addNavPic, getIndexProductPicName, addNavProductPic } from '@/api/nav'; // 从优化后的 nav.js 导入 (或 api/index.js)

// --- 状态定义 ---
const fixedCarouselImage = ref({
  id: 'fixed-image-0',
  url: 'http://image.langtaichem.com/NAV_IMG_2023-04-26_14.48.50_892_%E6%B5%B7%E9%B8%A5.jpg',
  name: 'NAV_IMG_2023-04-26_14.48.50_892_%E6%B5%B7%E9%B8%A5.jpg',
  status: 'success',
  isFixed: true
});
const userCarouselImages = ref([]);
const productImages = reactive({ middle: null, main: null, other: null });
const categories = ref([
  { key: 'middle', label: '中间体' },
  { key: 'main', label: '精细化学品' },
  { key: 'other', label: '其他' }
]);
const isSaving = ref(false);
const isUploading = ref(false);
const qiniuToken = ref('');
const maxFileSize = 5;
const carouselUploaderRef = ref(null);
const productImageUploaderRefs = reactive({});

// --- 计算属性 ---
const allCarouselImageUrlsForPreview = computed(() => {
  return [
    fixedCarouselImage.value.url,
    ...userCarouselImages.value.map(img => img.url)
  ];
});

// --- 生命周期钩子 & 数据获取 ---
onMounted(async () => {
  await fetchNavPicName();
  await fetchIndexProductPicName();
});

const fetchNavPicName = async () => {
  try {
    const response = await getNavPicName();
    if (response && response.data && response.data.allPic) {
      userCarouselImages.value = response.data.allPic
        .map(item => ({
          id: item.pictureId || Date.now() + Math.random(),
          url: `http://image.langtaichem.com/${item.pictureName}`,
          name: item.pictureName,
          status: 'success'
        }));
    } else {
        userCarouselImages.value = [];
    }
  } catch (error) {
    console.error('获取轮播图列表失败', error);
     userCarouselImages.value = [];
  }
};

const fetchIndexProductPicName = async () => {
    try {
        const response = await getIndexProductPicName();
        if (response && response.data) {
        const buildImageData = (name) => name ? { name: name, url: `http://image.langtaichem.com/${name}`, status: 'success' } : null;
        productImages.middle = buildImageData(response.data.intermediates);
        productImages.main = buildImageData(response.data.fineChemical);
        productImages.other = buildImageData(response.data.others);
        } else {
            productImages.middle = null;
            productImages.main = null;
            productImages.other = null;
        }
    } catch (error) {
        console.error('获取首页产品大图失败', error);
        productImages.middle = null;
        productImages.main = null;
        productImages.other = null;
    }
};

// --- 上传相关逻辑 ---
const beforeImageUpload = (rawFile) => {
    const validImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    const isImage = validImageTypes.includes(rawFile.type);
    const isLtMaxSize = rawFile.size / 1024 / 1024 < maxFileSize;

    if (!isImage) {
        ElMessage.error('只能上传图片文件（JPEG/PNG/GIF/WEBP）');
        return false;
    }
    if (!isLtMaxSize) {
        ElMessage.error(`图片大小不能超过 ${maxFileSize}MB`);
        return false;
    }
    return true;
};

const uploadFileCore = async (rawFile, qiniuPath, updateStateCallback) => {
    let currentToken = qiniuToken.value;
    let localPreviewUrl = null;
    try {
        if (!currentToken) {
            const tokenData = await getQiniuToken();
             if (typeof tokenData === 'string' && tokenData) {
                 currentToken = tokenData;
             } else if (tokenData && tokenData.data) {
                 currentToken = tokenData.data;
             } else {
                 throw new Error('获取到的Token格式不正确');
             }
            qiniuToken.value = currentToken;
        }
        localPreviewUrl = URL.createObjectURL(rawFile);
        updateStateCallback({
            name: rawFile.name,
            url: localPreviewUrl,
            status: 'uploading',
            displayUrl: localPreviewUrl
        });
        const uploadedKey = await uploadToQiniu(rawFile, currentToken, qiniuPath);
        const finalFileUrl = `http://image.langtaichem.com/${uploadedKey}`;
        updateStateCallback({
            name: uploadedKey,
            url: finalFileUrl,
            status: 'success'
        });
        ElMessage.success('图片上传成功');
    } catch (error) {
        console.error('上传失败:', error);
        ElMessage.error(`图片上传失败: ${error.message || '请重试'}`);
        updateStateCallback(null);
        throw error;
    } finally {
        if (localPreviewUrl) {
            URL.revokeObjectURL(localPreviewUrl);
        }
    }
};

const handleCarouselFileSelect = async (uploadFile) => {
    if (uploadFile.status !== 'ready' || !beforeImageUpload(uploadFile.raw) || isUploading.value) {
        clearUploaderFiles(carouselUploaderRef.value);
        return;
    }
    isUploading.value = true;
    const tempId = 'uploading-' + Date.now();
    let tempUploadingImage = reactive({ id: tempId, url: '', name: uploadFile.name, status: 'uploading', displayUrl: '' });
    userCarouselImages.value.push(tempUploadingImage);
    let uploadSuccess = false;

    try {
        await uploadFileCore(uploadFile.raw, 'carousel', (newState) => {
             if (newState && newState.status === 'success') {
                 const index = userCarouselImages.value.findIndex(img => img.id === tempId);
                 if (index !== -1) {
                     userCarouselImages.value.splice(index, 1, { ...newState, id: newState.id || Date.now() + Math.random() });
                 }
                 uploadSuccess = true;
             } else if (newState && newState.status === 'uploading') {
                 tempUploadingImage.displayUrl = newState.displayUrl;
                 tempUploadingImage.url = newState.url; // 同时更新 url 为本地预览，以便在上传时也能看到
             } else {
                 const index = userCarouselImages.value.findIndex(img => img.id === tempId);
                 if (index !== -1) {
                     userCarouselImages.value.splice(index, 1);
                 }
             }
        });
    } catch (error) {
        if (!uploadSuccess) {
             const index = userCarouselImages.value.findIndex(img => img.id === tempId);
             if (index !== -1) {
                 userCarouselImages.value.splice(index, 1);
             }
        }
    } finally {
       isUploading.value = false;
       clearUploaderFiles(carouselUploaderRef.value);
    }
};

const handleProductFileSelect = async (category, uploadFile) => {
    if (uploadFile.status !== 'ready' || !beforeImageUpload(uploadFile.raw) || isUploading.value) {
         clearUploaderFiles(productImageUploaderRefs[category]);
         return;
    }
    isUploading.value = true;
     try {
         await uploadFileCore(uploadFile.raw, 'index_product_pic', (newState) => {
             productImages[category] = newState;
         });
     } catch (error) {
        // ...
     } finally {
         isUploading.value = false;
         clearUploaderFiles(productImageUploaderRefs[category]);
     }
};

const clearUploaderFiles = (uploaderInstance) => {
    if (uploaderInstance && typeof uploaderInstance.clearFiles === 'function') {
        uploaderInstance.clearFiles();
    }
};

// --- 删除操作 ---
const deleteUserCarouselImage = (index) => {
  if (isUploading.value) return;
  userCarouselImages.value.splice(index, 1);
  ElMessage.success('轮播图已从列表移除');
};

const deleteProductImage = (category) => {
  if (isUploading.value) return;
  productImages[category] = null;
  ElMessage.success('产品图片已移除');
};

// --- 保存操作 ---
const saveCarouselImages = async () => {
  if (isSaving.value || isUploading.value) return;
  isSaving.value = true;
  try {
    const imageKeys = userCarouselImages.value
                        .filter(img => img && img.status === 'success' && img.name)
                        .map(img => img.name);
    await addNavPic({ pictureNameList: imageKeys });
    ElMessage.success('轮播图顺序及列表保存成功');
  } catch (error) {
    console.error('保存轮播图失败:', error);
  } finally {
    isSaving.value = false;
  }
};

const saveProductImages = async () => {
   if (isSaving.value || isUploading.value) return;
   isSaving.value = true;
   try {
     const payload = {
       intermediates: productImages.middle?.status === 'success' ? productImages.middle.name : null,
       fineChemical: productImages.main?.status === 'success' ? productImages.main.name : null,
       others: productImages.other?.status === 'success' ? productImages.other.name : null,
     };
     await addNavProductPic(payload);
     ElMessage.success('产品图片保存成功');
   } catch (error) {
     console.error('保存产品图片失败:', error);
   } finally {
     isSaving.value = false;
   }
};

// --- 拖拽结束 ---
const onCarouselDragEnd = () => {
  ElMessage.info('轮播图顺序已调整，请点击保存按钮生效');
};

</script>

<style scoped>
/* 包含了之前所有样式，并为固定图片和拖拽区域添加了新样式 */
.upload-images {
  display: flex;
  min-height: 100vh;
}

.main-content {
  margin-left: 200px; /* 根据 Sidebar 宽度调整 */
  flex: 1;
  padding: 30px;
  background: linear-gradient(135deg, #f5f7fa 0%, #e9ecef 100%);
}

.section {
  margin-bottom: 40px;
  background: #ffffff;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s ease;
}

.section:hover {
  transform: translateY(-5px);
}

.section-title {
  margin: 0 0 20px;
  color: #333;
  font-size: 20px;
  font-weight: 600;
  text-align: center;
  background: linear-gradient(90deg, #e9ecef 0%, #f5f7fa 100%);
  padding: 10px;
  border-radius: 8px;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.05);
}

.carousel-upload,
.product-images-container { /* 给产品图片区域一个容器，便于布局 */
  padding: 20px;
}

.upload-area {
  margin-bottom: 20px;
}

.upload-btn {
  background: #5a9bd4;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: background 0.3s ease, opacity 0.3s ease; /* 添加 opacity 过渡 */
}
.upload-btn:disabled {
    background-color: #a0cfff;
    cursor: not-allowed;
    opacity: 0.6; /* 禁用时更明显 */
}
.upload-btn:hover:not(:disabled) {
  background: #4a8bc2;
}

.image-list {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 20px;
  min-height: 50px;
  padding: 5px;
  /* border: 1px dashed #eee; */ /* 可选边框 */
}

/* 新增：固定图片和可拖拽区域的容器 */
.draggable-area {
    display: flex;
    flex-wrap: wrap;
    gap: 20px; /* 与 image-list 的 gap 保持一致 */
    flex-grow: 1; /* 占据剩余空间 */
}

.image-item {
  position: relative;
  width: 200px;
  height: 200px;
  border: 1px solid #eee;
  border-radius: 4px;
  overflow: hidden;
  background-color: #f9f9f9;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1); /* 添加轻微阴影 */
}
.draggable-area .image-item {
    cursor: move; /* 只有可拖拽项显示 move 指针 */
}
.image-item.fixed-image {
  cursor: default;
  border-color: #ccc; /* 固定图片边框稍深 */
}
.image-item .el-image {
    display: block;
    width: 100%;
    height: 100%;
}

.fixed-tag {
    position: absolute;
    top: 5px;
    left: 5px;
    background-color: rgba(0, 0, 0, 0.7); /* 背景更深 */
    color: white;
    font-size: 10px;
    padding: 3px 6px; /* 稍微增大 padding */
    border-radius: 3px;
    z-index: 10;
    font-weight: bold; /* 加粗 */
}

.delete-btn {
  position: absolute;
  top: 5px;
  right: 5px;
  background: rgba(229, 115, 115, 0.8);
  color: white;
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 12px;
  transition: background 0.3s ease, opacity 0.3s ease;
  z-index: 10;
  opacity: 0.8; /* 默认稍透明 */
}
.image-item:hover .delete-btn {
    opacity: 1; /* 悬浮时完全显示 */
}
.delete-btn:disabled {
    background-color: rgba(160, 160, 160, 0.7);
    cursor: not-allowed;
    opacity: 0.5; /* 禁用时更透明 */
}
.delete-btn:hover:not(:disabled) {
  background: #ef5350;
}

.upload-status {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    background-color: rgba(90, 155, 212, 0.8); /* 使用主题色 */
    color: white;
    font-size: 12px;
    text-align: center;
    padding: 4px 0; /* 增加 padding */
    z-index: 5;
    font-weight: 500;
}

.save-btn {
  background: #00c4b4;
  color: white;
  border: none;
  padding: 12px 25px;
  border-radius: 8px;
  cursor: pointer;
  display: block;
  margin: 20px auto 0;
  text-transform: uppercase;
  font-weight: 600;
  transition: background 0.3s ease, opacity 0.3s ease, transform 0.1s ease; /* 添加 transform 过渡 */
}
.save-btn:active:not(:disabled) {
    transform: scale(0.98); /* 点击时轻微缩小 */
}
.save-btn:disabled {
    background-color: #7fdfd8;
    cursor: not-allowed;
    opacity: 0.7;
}
.save-btn:hover:not(:disabled) {
  background: #00b3a4;
}

.product-images {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); /* 调整最小宽度 */
  gap: 25px; /* 增大间距 */
  margin-bottom: 20px;
}

.category-section {
  padding: 20px; /* 增加内边距 */
  border: 1px solid #e0e0e0; /* 边框颜色 */
  border-radius: 10px; /* 圆角更大 */
  background-color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: box-shadow 0.3s ease; /* 添加阴影过渡 */
}
.category-section:hover {
    box-shadow: 0 4px 12px rgba(0,0,0,0.08); /* 悬浮时阴影 */
}


.category-title {
  font-size: 17px; /* 稍大字体 */
  font-weight: 600;
  color: #333;
  margin-bottom: 20px; /* 增加下边距 */
  text-align: center;
}

.image-preview {
  position: relative;
  margin-top: 15px;
  width: 100%;
  aspect-ratio: 4 / 3; /* 定义宽高比，替代固定高度 */
  max-height: 450px; /* 仍然限制最大高度 */
  background-color: #f0f2f5;
  border-radius: 4px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}
.image-preview .el-image {
    display: block; /* 确保是块级元素 */
    max-width: 100%;
    max-height: 100%; /* 高度也限制为 100% */
    object-fit: contain; /* 保持比例 */
}

.no-image-placeholder {
    width: 100%;
    /* height: 200px; */ /* 让高度自适应或由 aspect-ratio 控制 */
    aspect-ratio: 4 / 3; /* 与预览区域保持一致 */
    max-height: 450px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #909399;
    font-size: 14px;
    border: 1px dashed #dcdfe6;
    border-radius: 4px;
    margin-top: 15px;
    background-color: #f8f9fa; /* 稍亮的背景 */
}

/* 响应式调整 */
@media (max-width: 992px) { /* 调整断点 */
  .product-images {
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); /* 中等屏幕调整 */
  }
}
@media (max-width: 768px) {
  .main-content {
      margin-left: 0; /* 小屏幕时移除左边距（如果 Sidebar 隐藏） */
      padding: 15px;
  }
  .product-images {
    grid-template-columns: 1fr; /* 小屏幕单列 */
  }
  .image-list {
      justify-content: center; /* 小屏幕时轮播图居中 */
  }
  .draggable-area {
      justify-content: center;
  }
}

/* 可以在这里添加 FontAwesome 的 @import 或确保在全局引入 */
/* @import url('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.4/css/all.min.css'); */

</style>